import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import CustomFieldDataType from "@mapmycustomers/shared/enum/CustomFieldDataType";
import Currency from "@mapmycustomers/shared/types/Currency";
import { EntityType, EntityTypeSupportingCustomFields } from "@mapmycustomers/shared/types/entity";

import SelectField from "@app/component/input/SelectField";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import {
  getCreateEditEntityType,
  getEditedCustomField,
} from "@app/scene/settings/component/CustomFields/store";
import {
  setCreateEditEntityType,
  setCustomFieldEditStep,
} from "@app/scene/settings/component/CustomFields/store/actions";
import CreateEditCustomFieldStep from "@app/scene/settings/enum/CreateEditCustomFieldStep";
import { getOrganizationSettingValue } from "@app/store/iam";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { getEntityTypeDisplayName } from "@app/util/ui";

import CustomFieldPreview from "../CustomFieldPreview";
import StepControl from "../StepControl";

import styles from "./BasicInfo.module.scss";
import CustomFieldTypes from "./CustomFieldTypes";

interface Props {
  analyticIssuer?: AnalyticsService;
  currencies: Currency[];
  defaultCurrencyCode?: string;
  editing?: boolean;
  selectedEntityType: EntityTypeSupportingCustomFields;
  setCreateEditEntityType: typeof setCreateEditEntityType;
  setStep: typeof setCustomFieldEditStep;
}

const BasicInfo: React.FC<Props> = ({
  currencies,
  defaultCurrencyCode,
  editing,
  selectedEntityType,
  setCreateEditEntityType,
  setStep,
}) => {
  const intl = useIntl();

  const handleNextStepClick = useCallback(() => {
    setStep(CreateEditCustomFieldStep.FIELD_SETUP);
  }, [setStep]);

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <SelectField<EntityTypeSupportingCustomFields>
          className={styles.formItem}
          disabled={editing}
          label={intl.formatMessage({
            id: "settings.customFields.createEditPane.basicInfo.selectObject",
            defaultMessage: "Select Object",
            description: "Settings Custom field - create/edit - basic info Select Object",
          })}
          onChange={setCreateEditEntityType}
          size="large"
          value={selectedEntityType}
        >
          {[EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL, EntityType.ACTIVITY].map(
            (entityType) => (
              <Select.Option key={entityType} value={entityType}>
                {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
              </Select.Option>
            )
          )}
        </SelectField>

        <Form.Item name={"dataType"}>
          <CustomFieldTypes editing={editing} />
        </Form.Item>

        <Form.Item dependencies={["dataType"]}>
          {({ getFieldValue, setFieldsValue }) => {
            const dataType = getFieldValue("dataType");

            if (dataType === CustomFieldDataType.MONETARY) {
              const defaultCurrency = currencies.find(
                (currency) => currency.code === defaultCurrencyCode
              );
              setFieldsValue({ currencyId: defaultCurrency?.id ?? null });
            } else {
              setFieldsValue({ currencyId: null });
            }

            return <CustomFieldPreview dataType={dataType} />;
          }}
        </Form.Item>
      </div>

      <StepControl onNext={handleNextStepClick} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currencies: getCurrencies(state),
  defaultCurrencyCode: getOrganizationSettingValue(state)(OrganizationSetting.CURRENCY),
  selectedEntityType: getCreateEditEntityType(state),
  type: getEditedCustomField(state).dataType,
});

const mapDispatchToProps = {
  setCreateEditEntityType,
  setStep: setCustomFieldEditStep,
};

export default connect(mapStateToProps, mapDispatchToProps)(BasicInfo);
